import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, ExternalLink, Lock, Layers, Globe2, FileCode, Workflow, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LangSwitcher from '@/components/LangSwitcher';
import { useAuth } from '@/context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const DOCS_URL = `${API_BASE}/api/docs`;
const OPENAPI_URL = `${API_BASE}/api/openapi.json`;

const HIGHLIGHTS = [
    {
        icon: FileCode,
        title: 'API REST + OpenAPI 3.1',
        desc: 'Endpoints documentés en temps réel via FastAPI. JSON pur, types stricts, codes HTTP standards.',
    },
    {
        icon: Lock,
        title: 'Authentification Supabase',
        desc: 'JWT signés, RLS PostgreSQL multi-tenant, audit-trail immuable sur chaque mutation.',
    },
    {
        icon: Layers,
        title: 'Modulaire & versionné',
        desc: 'Routers spécialisés (auth, projects, meetings, super-admin, plans, transcription…) ; pas de monolithe.',
    },
    {
        icon: Workflow,
        title: 'Intégrations métier',
        desc: 'GANTT, budget, GED, visioconférence, exports white-label PDF/XLSX/PPTX, IA Claude 3.5 et Whisper.',
    },
    {
        icon: Globe2,
        title: 'Souveraineté & RLS',
        desc: 'Isolation stricte par espace client. Vos données ne franchissent jamais la frontière de votre workspace.',
    },
    {
        icon: Sparkles,
        title: 'Webhooks à venir',
        desc: 'Notifications sortantes (projet.created, facture.due, meeting.cr_emailed) — roadmap 2026.',
    },
];

const USE_CASES = [
    {
        eyebrow: 'Bailleurs',
        title: 'Suivi de portefeuilles multi-pays',
        desc: 'Agrégez les KPIs de centaines de projets cofinancés via un même endpoint /dashboard.pdf, déjà brandé avec votre logo.',
    },
    {
        eyebrow: 'Ministères & collectivités',
        title: 'SI projet centralisé',
        desc: 'Branchez votre SI ministériel sur les workflows GANTT/Budget/GED pour remonter automatiquement les indicateurs de votre programme national.',
    },
    {
        eyebrow: 'ONG & associations',
        title: 'Reporting bailleurs en 1 clic',
        desc: 'Générez les comptes-rendus de réunion, attestations d\'achèvement et rapports financiers à la volée — multilingue.',
    },
    {
        eyebrow: 'Intégrateurs',
        title: 'Plateforme d\'orchestration',
        desc: 'Construisez vos propres connecteurs (ERP, Power BI, CRM, signature électronique) sur une API stable et documentée.',
    },
];

export default function ApiIntegrations() {
    const { session } = useAuth();
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    // Désindexation SEO — la page n'est plus linkée publiquement (déplacée dans Super Admin).
    useEffect(() => {
        const tags = [
            { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet' },
            { name: 'googlebot', content: 'noindex, nofollow' },
        ];
        const added = tags.map(({ name, content }) => {
            let el = document.querySelector(`meta[name="${name}"]`);
            const created = !el;
            if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
            const prev = el.getAttribute('content');
            el.setAttribute('content', content);
            return { el, prev, created };
        });
        return () => {
            // Restaurer pour ne pas polluer les autres pages SPA
            added.forEach(({ el, prev, created }) => {
                if (created) el.remove();
                else if (prev != null) el.setAttribute('content', prev);
            });
        };
    }, []);

    useEffect(() => {
        if (!session?.access_token) { setIsSuperAdmin(false); return; }
        let cancelled = false;
        axios.get(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${session.access_token}` } })
            .then((r) => { if (!cancelled) setIsSuperAdmin(!!r.data?.is_super_admin); })
            .catch(() => { if (!cancelled) setIsSuperAdmin(false); });
        return () => { cancelled = true; };
    }, [session?.access_token]);

    const docsLinkWithToken = isSuperAdmin && session?.access_token
        ? `${DOCS_URL}?token=${encodeURIComponent(session.access_token)}`
        : null;
    const openapiLinkWithToken = isSuperAdmin && session?.access_token
        ? `${OPENAPI_URL}?token=${encodeURIComponent(session.access_token)}`
        : null;

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="page-api">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src="/african-valley-logo.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover ring-1 ring-border shadow-soft" />
                        <span className="flex items-baseline gap-2">
                            <span className="font-heading text-2xl font-semibold tracking-tight">African</span>
                            <span className="font-heading text-2xl text-primary font-medium">Valley</span>
                            <span className="font-heading text-xs uppercase tracking-[0.18em] text-foreground/50 ml-1.5 hidden sm:inline">Projets 360°</span>
                        </span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <LangSwitcher />
                        <Link to="/" className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-1">
                            <ArrowLeft size={14} /> Accueil
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero */}
                <section className="border-b border-border">
                    <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-7">
                            <div className="eyebrow mb-5 text-primary">API & Intégrations</div>
                            <h1 className="font-heading text-4xl lg:text-5xl leading-tight">
                                Branchez votre SI sur African Valley.
                                <span className="block text-primary mt-2">Une API souveraine, documentée, prête pour vos appels d'offres B2G.</span>
                            </h1>
                            <p className="mt-7 text-lg text-foreground/75 leading-[1.8]">
                                Notre architecture modulaire <strong>FastAPI + Supabase</strong> expose tous les flux
                                pilotage, finance, GED, IA et visioconférence via des endpoints REST documentés.
                                Idéal pour les <strong>bailleurs internationaux, ministères, ONG</strong> et intégrateurs
                                qui veulent fédérer leurs outils existants sans casser leur SI.
                            </p>
                            <div className="mt-9 flex flex-wrap gap-3 items-center" data-testid="api-hero-cta">
                                {docsLinkWithToken ? (
                                    <a href={docsLinkWithToken} target="_blank" rel="noopener noreferrer">
                                        <Button size="lg" className="bg-primary hover:bg-primary-hover" data-testid="api-docs-btn">
                                            <Lock size={14} className="mr-1.5" /> Doc Swagger (Super Admin) <ExternalLink size={14} className="ml-2" />
                                        </Button>
                                    </a>
                                ) : (
                                    <Button size="lg" className="bg-primary hover:bg-primary-hover" data-testid="api-contact-primary-btn"
                                        onClick={() => { window.location.href = '/p/contact?topic=api-key'; }}>
                                        Demander un accès API <ArrowRight size={14} className="ml-2" />
                                    </Button>
                                )}
                                <Link to="/p/contact">
                                    <Button size="lg" variant="outline" data-testid="api-contact-btn">
                                        Parler à un expert
                                    </Button>
                                </Link>
                                {openapiLinkWithToken && (
                                    <a href={openapiLinkWithToken} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center text-sm text-foreground/60 hover:text-foreground self-center ml-2"
                                        data-testid="api-openapi-link">
                                        Schéma OpenAPI brut <ExternalLink size={12} className="ml-1" />
                                    </a>
                                )}
                                {!isSuperAdmin && (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground/55 self-center ml-2" data-testid="api-restricted-note">
                                        <Lock size={11} /> La documentation technique complète est réservée aux titulaires d'une clé d'API.
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-ink/15 bg-ink p-6 font-mono text-xs text-emerald-200 shadow-2xl overflow-hidden">
                                <div className="flex gap-1.5 mb-4 opacity-60">
                                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                                </div>
                                <pre className="whitespace-pre-wrap leading-relaxed text-[11px]">
{`# Récupérer le dashboard PDF d'un espace
curl -X GET "$API_URL/api/exports/\\
dashboard/{workspace_id}.pdf" \\
  -H "Authorization: Bearer $JWT" \\
  -o dashboard.pdf

# 200 OK · application/pdf
# branding white-label appliqué
# KPIs + projets + audit récents`}
                                </pre>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Highlights */}
                <section className="border-b border-border">
                    <div className="max-w-6xl mx-auto px-6 py-16">
                        <div className="eyebrow mb-3 text-primary">Capacités techniques</div>
                        <h2 className="font-heading text-3xl lg:text-4xl mb-12 max-w-2xl">
                            Une API construite pour la <span className="text-secondary">production</span>, pas pour la démo.
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {HIGHLIGHTS.map((h, i) => {
                                const Icon = h.icon;
                                return (
                                    <article
                                        key={i}
                                        className="p-6 rounded-xl border border-border bg-white hover:border-primary/30 transition-colors"
                                        data-testid={`api-highlight-${i}`}
                                    >
                                        <div className="h-10 w-10 rounded-lg bg-primary-light text-primary flex items-center justify-center mb-4">
                                            <Icon size={18} strokeWidth={1.6} />
                                        </div>
                                        <h3 className="font-heading text-lg leading-tight mb-2">{h.title}</h3>
                                        <p className="text-sm text-foreground/70 leading-relaxed">{h.desc}</p>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Use cases */}
                <section className="border-b border-border bg-surface">
                    <div className="max-w-6xl mx-auto px-6 py-16">
                        <div className="eyebrow mb-3 text-primary">Cas d'usage partenaires</div>
                        <h2 className="font-heading text-3xl lg:text-4xl mb-12 max-w-2xl">
                            Conçue pour les <span className="text-secondary">programmes ambitieux</span>.
                        </h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {USE_CASES.map((u, i) => (
                                <article
                                    key={i}
                                    className="p-7 rounded-xl border border-border bg-background"
                                    data-testid={`api-usecase-${i}`}
                                >
                                    <div className="eyebrow text-secondary mb-3">{u.eyebrow}</div>
                                    <h3 className="font-heading text-2xl mb-3">{u.title}</h3>
                                    <p className="text-foreground/75 leading-[1.7]">{u.desc}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section>
                    <div className="max-w-4xl mx-auto px-6 py-16 text-center">
                        <h3 className="font-heading text-3xl">Vous lancez un programme africain en 2026 ?</h3>
                        <p className="text-foreground/70 mt-3 max-w-2xl mx-auto leading-[1.7]">
                            Notre équipe accompagne les <strong>bailleurs et ministères</strong> dans l'intégration sur mesure
                            de l'API — du POC en 5 jours à la production multi-pays sous 6 semaines.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                            <Link to="/p/contact">
                                <Button size="lg" className="bg-primary hover:bg-primary-hover" data-testid="api-cta-contact">
                                    Parler à un architecte <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </Link>
                            {docsLinkWithToken && (
                                <a href={docsLinkWithToken} target="_blank" rel="noopener noreferrer">
                                    <Button size="lg" variant="outline" data-testid="api-cta-docs">
                                        <Lock size={13} className="mr-1.5" /> Explorer la doc (Super Admin)
                                    </Button>
                                </a>
                            )}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}
